import { createAsyncThunk } from "@reduxjs/toolkit"
import { fetchBackend } from "@/core/apis/backend"

export interface BankQuestionSuggestion {
  bankId: string
  question: string
  tags: string[]
  responseCount: number
  isReverse: boolean
  //Mode across this row's linked instances, null if none yet — same aggregation blind spot as
  //isReverse above, named scalePoints (not responseCount, already taken above for volume).
  scalePoints: number | null
  similarity: number
}

export interface BankQuestionSuggestResult {
  suggestions: BankQuestionSuggestion[]
  // The row this exact text already belongs to, if any — set instead of any suggestions being
  // returned, since every one of them would be refused by linkTextToBankQuestion()'s conflict
  // guard (or be a pointless re-confirm on the home row itself). More reliable than a question's
  // own bankId from getTemplateBenchmarkStatus, which only reflects a row that already cleared
  // the calc_benchmarks threshold (Tom, 2026-09-11 — KB features/bank-questions-vision.md §29).
  alreadyLinkedTo: string | null
}

const EMPTY_SUGGEST_RESULT: BankQuestionSuggestResult = {
  suggestions: [],
  alreadyLinkedTo: null
}

//Editor variant is supervisor-gated via supervisorConnectedId — not /supervisor/bank-questions/*
//(that requires the supervisor JWT, unavailable from /template/editor while "connected as" a client).
//asSupervisor = raw supervisor variant (/supervisor/* JWT)
export const suggestBankQuestions = createAsyncThunk(
  "bankQuestion/suggest",
  async ({ text, asSupervisor }: { text: string; asSupervisor?: boolean }) => {
    const res = await fetchBackend({
      url: `${asSupervisor ? "/supervisor" : ""}/bank-questions/suggest`,
      method: "POST",
      data: { text }
    })
    if (res.error) return EMPTY_SUGGEST_RESULT
    return res as BankQuestionSuggestResult
  }
)

export const linkTextToBankQuestion = createAsyncThunk(
  "bankQuestion/link",
  async ({ bankId, text }: { bankId: string; text: string }) => {
    const res = await fetchBackend({ url: `/bank-questions/${bankId}/link`, method: "POST", data: { text } })
    if (res.error) return { bankId, added: 0, error: true }
    return { bankId, added: res?.added ?? 0, conflictBankId: res?.conflictBankId ?? null, error: false }
  }
)

export const linkTextToBankQuestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/linkAsSupervisor",
  async ({ bankId, text, lang }: { bankId: string; text: string; lang?: string }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/link`, method: "POST", data: { text, lang } })
    if (res.error) return { bankId, added: 0, error: true }
    return { bankId, added: res?.added ?? 0, conflictBankId: res?.conflictBankId ?? null, error: false }
  }
)

export const unlinkTextFromBankQuestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/unlinkAsSupervisor",
  async ({ bankId, text, lang }: { bankId: string; text: string; lang?: string }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/unlink`, method: "POST", data: { text, lang } })
    //`removed` can legitimately be 0 (server refuses to remove the last remaining phrasing) — the
    //caller must check it, not assume success just because there was no error
    return { bankId, text, error: !!res.error, removed: res?.removed ?? 0 }
  }
)

//Moves a phrasing to index 0 — listBankQuestions() displays texts.fr[0] as each row's
//representative text in the "Banque de questions" table, this is how a supervisor picks which
//phrasing shows there (KB §36).
export const setPrimaryBankQuestionText_AsSupervisor = createAsyncThunk(
  "bankQuestion/setPrimaryAsSupervisor",
  async ({ bankId, text, lang }: { bankId: string; text: string; lang?: string }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/set-primary`, method: "POST", data: { text, lang } })
    return { bankId, text, error: !!res.error, success: !!res?.success }
  }
)

//Absorbs sourceIds into targetId — checkbox-select in the "Banque de questions" list, KB §36/§37.
export const mergeBankQuestions_AsSupervisor = createAsyncThunk(
  "bankQuestion/mergeAsSupervisor",
  async ({ targetId, sourceIds }: { targetId: string; sourceIds: string[] }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${targetId}/merge`, method: "POST", data: { sourceIds } })
    return { error: !!res.error, success: !!res?.success }
  }
)

//alreadyExists: true when the backend found a normalized-text match instead of creating a new
//row (dedup guard) — caller should toast differently ("déjà dans la banque" vs "créée")
export const createBankQuestion = createAsyncThunk(
  "bankQuestion/create",
  async (text: string) => {
    const res = await fetchBackend({ url: "/bank-questions/create", method: "POST", data: { text } })
    if (res.error) return { id: null, alreadyExists: false, error: true }
    return { id: res?.id as string, alreadyExists: !!res?.alreadyExists, error: false }
  }
)

export const createBankQuestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/createAsSupervisor",
  async ({ text, lang }: { text: string; lang?: string }) => {
    const res = await fetchBackend({ url: "/supervisor/bank-questions/create", method: "POST", data: { text, lang } })
    if (res.error) return { id: null, alreadyExists: false, error: true }
    return { id: res?.id as string, alreadyExists: !!res?.alreadyExists, error: false }
  }
)

export const deleteBankQuestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/deleteAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/delete`, method: "POST" })
    return { bankId, error: !!res.error, detachedCount: res?.detachedCount || 0 }
  }
)

export interface BankQuestionListRow {
  id: string
  text: string
  textsCount: number
  tags: string[] | null
  translationsCount: number
  hasBenchmark: boolean
  isReverse: boolean
  scalePoints: number | null
}

export interface BankQuestionListResult {
  rows: BankQuestionListRow[]
  total: number
  page: number
  pageSize: number
}

const EMPTY_LIST_RESULT: BankQuestionListResult = { rows: [], total: 0, page: 1, pageSize: 20 }

//"Banque de questions" back-office screen (2026-09-14) — raw supervisor only, /supervisor/benchmark
//is never reached "connected as" a client, so no supervisorConnectedId variant needed here.
export const listBankQuestions_AsSupervisor = createAsyncThunk(
  "bankQuestion/listAsSupervisor",
  async ({ search, page, pageSize }: { search: string; page: number; pageSize: number }) => {
    const res = await fetchBackend({
      url: "/supervisor/bank-questions",
      data: {
        page,
        pageSize,
        ...(search.trim() ? { search: search.trim() } : {})
      }
    })
    if (res.error) return EMPTY_LIST_RESULT
    return res as BankQuestionListResult
  }
)

export interface BankScanSuggestion {
  //One entry per language actually present on that live question (fr always, en/de/es whenever
  //that live question's own name jsonb has them) — similarity is computed on fr only (KB §36)
  texts: Record<string, string>
  isReverse: boolean
  //Scale cardinality (5 = default Likert, 10 = the NPS 0-10 scale) — not a response volume,
  //named scalePoints rather than responseCount to avoid the mixup with BankQuestionSuggestion's
  //own responseCount below, which means something else entirely.
  scalePoints: number
  similarity: number
}

//"Scanner" a bank_questions row for similar live template questions not already known anywhere
//in the catalog — reverse direction of suggestBankQuestions() (KB features/bank-questions-vision.md
//§36). Used by the manage modal's own scan/associate flow, raw supervisor only.
export const scanBankQuestionSimilar_AsSupervisor = createAsyncThunk(
  "bankQuestion/scanAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/scan` })
    if (res.error) return { suggestions: [] as BankScanSuggestion[] }
    return res as { suggestions: BankScanSuggestion[] }
  }
)

//"Pas la même question" on a scan suggestion (2026-09-17, Ben) — scanBankQuestionSimilar_AsSupervisor
//stops offering it afterwards. Lang defaults to fr server-side (the scan itself only ever compares
//fr text).
export const excludeBankQuestionSuggestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/excludeAsSupervisor",
  async ({ bankId, text }: { bankId: string; text: string }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/exclude`, method: "POST", data: { text } })
    return { bankId, text, error: !!res.error, success: !!res?.success }
  }
)

//Undoes excludeBankQuestionSuggestion_AsSupervisor — the "Réintégrer" button in the Exclusions tab.
export const removeBankQuestionExclusion_AsSupervisor = createAsyncThunk(
  "bankQuestion/removeExclusionAsSupervisor",
  async ({ bankId, text }: { bankId: string; text: string }) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/exclusions/remove`, method: "POST", data: { text } })
    return { bankId, text, error: !!res.error, success: !!res?.success }
  }
)

//Full per-language texts object ({fr: [...], en: [...], ...}) — the manage modal's own
//multi-language view. excludedTexts ({fr: [...]}) is the "Exclusions"
//tab's own content — kept out of `texts` server-side so it never gets counted as a real phrasing.
export const fetchBankQuestionAllTexts_AsSupervisor = createAsyncThunk(
  "bankQuestion/fetchAllTextsAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/all-texts` })
    if (res.error) return { bankId, texts: {} as Record<string, string[]>, excludedTexts: {} as Record<string, string[]> }
    return { bankId, texts: res.texts as Record<string, string[]>, excludedTexts: res.excludedTexts as Record<string, string[]> }
  }
)

//Recalculates just this one bank_questions row instead of the whole catalog (2026-09-17, Ben —
//per-row calculator button). Same computation as the page-level "Calculer" question step, scoped.
export const calculateOneBankQuestion_AsSupervisor = createAsyncThunk(
  "bankQuestion/calculateOneAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/calculate`, method: "POST" })
    return { bankId, error: !!res.error }
  }
)

export interface BankQuestionBenchmarkSummaryRow {
  sector: string
  size: string
  value: number
  count: number
}

//"Benchmark" tab recap (2026-09-17, Ben) — plain readout of every calc_benchmarks row for this
//bank_questions row, one per sector plus "all".
export const fetchBankQuestionBenchmarkSummary_AsSupervisor = createAsyncThunk(
  "bankQuestion/benchmarkSummaryAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/benchmark-summary` })
    if (res.error) return { bankId, rows: [] as BankQuestionBenchmarkSummaryRow[] }
    return { bankId, rows: res as BankQuestionBenchmarkSummaryRow[] }
  }
)

export interface BankQuestionTemplateUsage {
  templateId: string
  templateName: string
  fromSupervisor: boolean
  accountId: string | null
  accountName: string | null //null when fromSupervisor — an IDTree master template, never duplicated into a client account
}

//"Utilisations" tab — every live template currently using one of this row's fr phrasings.
export const fetchBankQuestionTemplateUsage_AsSupervisor = createAsyncThunk(
  "bankQuestion/templateUsageAsSupervisor",
  async (bankId: string) => {
    const res = await fetchBackend({ url: `/supervisor/bank-questions/${bankId}/templates` })
    if (res.error) return { bankId, usages: [] as BankQuestionTemplateUsage[] }
    return { bankId, usages: res.usages as BankQuestionTemplateUsage[] }
  }
)
